<template>
  <div class="container report">
    <div class="report__title-container">
      <div class="report__title-left">
        <div class="report__header-button-wrapper"
             v-if="isDataFetched">
          <button
            @click.stop="cancelSortingAndFilters"
            class="report__clear-filters">
            <span>clear all filters and sorting</span>
            <span class="report__cross">&times;</span>
          </button>
        </div>

      </div>
      <div class="report__title-right"
           v-if="isDataFetched">
        <div class="report__reports">
          <span class="report__count">Exports: {{totalItems}}</span>
          <base-select
            class="report__grid-select"
            :options="paginationOptions"
            alignLeft
            :titleStyle="{'padding-left': '5px'}"
            :headerStyle="{}"
            showTopBorder
            shiftLabelRight
            v-model="pageItemsLimit"
            :selectStyle="{'max-height': '35px !important', width: '100px !important'}"
            @change="handleLimitChange"
          />
        </div>
        <button class="report__button" @click="openCreateModal()">
          Create Export
        </button>
      </div>
    </div>

    <div class="common-table report__table">
      <div class="common-table__row common-table__header"
           v-if="isDataFetched">
        <div class="common-table__header-cell"
             v-for="column in reportTableColumnsConfig"
             :key="column.property"
             :class="{[`report__${column.property}`]: true, 'report__navbar-opened': !isNavbarCollapsed}">
          <base-multi-select-sortable-header
            v-if="column.property !== 'download_link'"
            class="report__select-header"
            :column="column"
            :sort-config="sortConfig"
            :predefinedOptions="filterParams[column.property]"
            :predefinedNumberFilter="['id'].includes(column.property) ? filterParams[column.property] : null"
            :predefinedDateRangeFilter="['timestamp_submitted', 'timestamp_processed', 'timestamp_failed', 'timestamp_completed'].includes(column.property) ? {start: filterParams[column.property]['date_from'], end: filterParams[column.property]['date_to']}: null"
            @sort="onHeaderCellClicked(column, $event)"
            @apply="filterColumn($event, column)"
            @cancel="cancelFilter($event, column)"
            :ref="sortableHeaderRefs[column.property]"
          />
        </div>
      </div>
      <template v-if="dataMissing">
        <div class="common-table__row no-data-block">
          No records found
        </div>
      </template>
      <div class="" v-else-if="!isDataFetched">
        <div class="report__loader">
          <base-loader/>
        </div>
      </div>
      <template v-else>
        <template v-for="report in paginatedReportings">
          <div :key="report.id" class="common-table__row">
            <div class="common-table__cell"
                 v-for="column in reportTableColumnsConfig"
                 :key="column.property"
                 :class="[`report__${column.property}`, `report__row_${column.property}`]">
              <template v-if="column.property === 'download_link' && report[column.property]">
                <a :href="report[column.property]" class="download-icon" target="_blank"
                   v-tooltip.bottom-start="{content: 'Click to download', ...tooltipOptions}">
                  <base-material-design-icon icon="fileDownload"/>
                </a>
              </template>
              <template v-else>
                  <span>
                    {{ report[column.property] }}
                  </span>
              </template>
            </div>
          </div>
        </template>
      </template>
    </div>
    <div class="pagination-wrapper" v-show="hasPagination">
      <base-pagination :limit="pageItemsLimit"
                       :total-items="totalItems"
                       @pageChanged="pageChanged"/>
    </div>
    <div class="report__update-loader" v-if="isDataUpdating">
      <base-loader class="report__update-loader-ring"/>
    </div>

    <modal name="create"
           v-bind="changeReportModalProps">
      <CreateReportPopup
        ref="create-report"
        @apply="createReport($event)"
        @cancel="closeCreateModal"
        :opts="opts"
        :dates="dates"
      />
    </modal>
  </div>
</template>

<script>
import helper from '@/services/Helper'
import { mapActions, mapState } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'
import moment from 'moment'
import CreateReportPopup from './CreateReportPopup'
import _ from 'lodash'
import BaseSortMixins from '@/common/BaseSortMixins'

const ITEMS_PER_PAGE = 10
const EMPTY_FILTERS = {
  id: {
    operator: null,
    value: null
  },
  report_category: [],
  timestamp_submitted: {
    date_from: null,
    date_to: null
  },
  timestamp_processed: {
    date_from: null,
    date_to: null
  },
  timestamp_failed: {
    date_from: null,
    date_to: null
  },
  timestamp_completed: {
    date_from: null,
    date_to: null
  },
  sort_column: [],
  sort_order: [],
  page_from: 0,
  page_to: ITEMS_PER_PAGE
}
const PAGINATION_OPTIONS = [
  {
    label: '10',
    value: 10
  },
  {
    label: '25',
    value: 25
  },
  {
    label: '50',
    value: 50
  }
]
const DEFAULT_CONFIG = [
  {
    label: '',
    property: 'download_link'
  },
  {
    label: 'ID',
    property: 'id',
    isNumber: true
  },
  {
    label: 'Report Category',
    property: 'report_category',
    isSmartInput: false,
    options: []
  },
  {
    label: 'Submitted',
    property: 'timestamp_submitted'
  },
  {
    label: 'Processed',
    property: 'timestamp_processed'
  },
  {
    label: 'Failed',
    property: 'timestamp_failed'
  },
  {
    label: 'Completed',
    property: 'timestamp_completed'
  }
]

export default {
  name: 'Reporting',
  components: { CreateReportPopup },
  mixins: [BaseSortMixins],
  data () {
    return {
      isDataFetched: false,
      isDataUpdating: false,
      filteredReports: [],
      sortConfig: [],
      pageItemsLimit: ITEMS_PER_PAGE,
      totalItems: 0,
      reportTableColumnsConfig: _.cloneDeep(DEFAULT_CONFIG),
      filterParams: _.cloneDeep(EMPTY_FILTERS),
      sortableHeaderRefs: {
        'id': 'id',
        'report_category': 'report_category',
        'timestamp_submitted': 'timestamp_submitted',
        'timestamp_processed': 'timestamp_processed',
        'timestamp_failed': 'timestamp_failed',
        'timestamp_completed': 'timestamp_completed'
      },
      paginationOptions: _.cloneDeep(PAGINATION_OPTIONS)
    }
  },
  computed: {
    ...mapState('reporting', ['opts', 'reports', 'settings']),
    dataMissing () {
      return this.isDataFetched && !this.reports.length
    },
    dates () {
      return _.get(this.settings, 'report_dates', []).map(item => new Date(item))
    },
    changeReportModalProps () {
      const changeReportModalProps = {
        width: 690
      }
      return { ...baseModalProps, ...changeReportModalProps }
    },
    hasPagination () {
      return (this.isDataFetched && !this.isDataUpdating) && this.totalItems > this.pageItemsLimit
    },
    paginatedReportings () {
      let reports = _.cloneDeep(this.reports)
      // eslint-disable-next-line camelcase
      const { page_from = 0, page_to = 100 } = this.filterParams
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.totalItems = reports.length
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.filteredReports = reports
      return reports.slice(page_from, page_to)
    }
  },
  watch: {
    settings: {
      deep: true,
      handler (settings) {
        const reportCategory = settings.report_category.map(helper.convertListToOptions)
        this.reportTableColumnsConfig.find(item => item.property === 'report_category').options = reportCategory
      }
    },
    clientId: {
      handler (v) {
        v && this.loadData()
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('reporting', ['getReportData', 'createNewReport', 'getReportSettings', 'getReportOptions']),
    async loadData () {
      this.isDataFetched && (this.isDataUpdating = true)
      await Promise.all([
        this.getReportData({
          clientId: this.clientId,
          filters: this.filterParams
        }),
        this.getReportOptions(),
        this.getReportSettings(this.clientId)
      ])
      this.isDataUpdating = false
      this.isDataFetched = true
    },
    pageChanged ({ from, to }) {
      this.filterParams.page_from = from
      this.filterParams.page_to = to
    },
    async createReport (report) {
      this.isDataUpdating = true
      if (report.start_date && report.start_date !== 'clear') {
        report.start_date = moment(report.start_date).format('YYYY-MM-DD').toString()
      } else {
        report.start_date = null
      }
      if (report.end_date && report.end_date !== 'clear') {
        report.end_date = moment(report.end_date).format('YYYY-MM-DD').toString()
      } else {
        report.end_date = null
      }

      try {
        await this.createNewReport({ clientId: this.clientId, report })
        await this.loadData()
        this.closeCreateModal()
      } catch (e) {
        this.$refs['create-report'].showErrors(e[0])
      } finally {
        this.isDataUpdating = false
      }
    },
    closeCreateModal () {
      this.$modal.hide('create')
    },
    openCreateModal (reportId) {
      this.$modal.show('create')
    },
    handleLimitChange (value) {
      this.pageItemsLimit = value
      this.filterParams = { ...this.filterParams, page_from: 0, page_to: value }
    },
    filterColumn (event, column) {
      if (column.property === 'timestamp_submitted' ||
        column.property === 'timestamp_processed' ||
        column.property === 'timestamp_failed' ||
        column.property === 'timestamp_completed') {
        this.filterParams[column.property].date_from = event.date_from
        this.filterParams[column.property].date_to = event.date_to
      } else {
        this.filterParams[column.property] = event
      }
      this.filterParams.page_from = 0
      this.filterParams.page_to = this.pageItemsLimit
      this.loadData()
    },
    onHeaderCellClicked (cell, direction, declineCall) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortDirection = direction
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property
        }]
      }
      this.filterParams = {
        ...this.filterParams,
        sort_column: this.sortConfig.map(item => item.propertyName),
        sort_order: this.sortConfig.map(item => item.sortDirection)
      }
      !declineCall && this.loadData()
    },
    cancelSortingAndFilters: function () {
      const emptyFilters = _.cloneDeep(EMPTY_FILTERS)
      this.filterParams = { ...this.filterParams, ...emptyFilters, page_to: this.pageItemsLimit }
      Object.keys(this.sortableHeaderRefs)
        .forEach(ref => {
          const [element] = this.$refs[ref]
          element.$refs[element.childRef].cancel(true)
        })
      this.isOptimized = {
        yes: false,
        no: false
      }
      this.sortConfig = []
      !this.isDataUpdating && this.loadData()
    },
    cancelFilter (event, column) {
      if (column.property === 'timestamp_submitted' ||
        column.property === 'timestamp_processed' ||
        column.property === 'timestamp_failed' ||
        column.property === 'timestamp_completed') {
        this.filterParams[column.property].date_from = null
        this.filterParams[column.property].date_to = null
      } else {
        this.filterParams[column.property] = null
      }
      this.loadData()
    }
  }
}
</script>

<style scoped lang="scss">

.common-table {
  border-radius: $rfx-default-border-radius;
  overflow: visible;

  &__header {
    background-color: rgba(1, 22, 39, 0.1);
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
  }

  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
    padding: 0;
    height: 60px;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  &__cell {
    color: $rfx-dark-march;
    height: 60px;
    padding: 0;
    display: flex;
    align-items: center;
    font-size: 14px;
    border-bottom: 1px solid $rfx-light-steel;

    &:first-child {
      padding-left: 25px;
    }
  }

  &__row {
    border-left: 1px solid $rfx-light-steel;
    border-right: 1px solid $rfx-light-steel;
  }
}

.report {
  position: relative;
  min-height: 200vh !important;

  &__navbar-opened {
    &.report__id.common-table__header-cell {
      flex-basis: 8.2%;
    }
  }

  &__cross {
    font-size: 21px;
    margin-left: 5px;
    margin-top: -2px;
  }

  &__title-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 50%;
    height: 60px;
  }

  &__title-right > *:not(:last-child) {
    margin-right: 30px;
  }

  &__title-left {
    display: flex;
    align-items: center;
  }

  &__title-left > *:not(:last-child) {
    margin-right: 30px;
  }

  &__header-button-wrapper {
    border-radius: 15px;
    border: solid 1px $rfx-light-steel;
    background: white;
    height: 28px;
    display: flex;
    padding: 0 10px;
  }

  &__copy-button {
    flex: 1 1 50%;
    height: 100%;
    border: none;
    outline: none;
    color: $rfx-dark-march;
    white-space: nowrap;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 90px;
    background-color: #fff;
    font-size: 11px;

    &:hover {
      opacity: 0.5;
    }
  }

  &__clear-filters {
    @extend .report__copy-button;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: auto;

    span:first-child {
      font-size: 11px;
    }
  }

  &__grid-select {
    flex-basis: 100px;
  }

  &__title-container {
    padding: 14px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__loader {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 530px);
  }

  &__table {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  &__update-loader {
    position: absolute;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    background-color: white;
    top: 0;
    opacity: 0.5;
    z-index: 10000;
  }

  &__update-loader-ring {
    margin-top: 200px;
  }

  &__reports {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }

  &__download_link {
    flex: 0 0 45px;

    &.common-table__header-cell {
      flex-basis: 0;
    }
  }

  &__id {
    flex: 0 0 75px;

    &.common-table__header-cell {
      flex-basis: 7.5%;

      &::v-deep .multi-select__title-container {
        padding-left: 70px;
      }
    }
  }

  &__report_category,
  &__timestamp_submitted,
  &__timestamp_processed,
  &__timestamp_failed,
  &__timestamp_completed {
    flex: 0 0 16%;
  }

  &__report_category {
    flex: 1;
  }

  &__button {
    width: 136px;
    padding: 0 20px;
    height: 60px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    border: none;
    outline: none;
    cursor: pointer;
    background-color: $rfx-medium-march;
    color: #fff;
  }

  &__count {
    color: #b2b9be;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    width: 75px;
    white-space: nowrap;
  }
}

.pagination-wrapper {
  display: flex;
  height: 60px;
  margin-top: 10px;
  justify-content: center;
  align-items: center;
  border: 1px solid $rfx-light-steel;
  border-radius: $rfx-default-border-radius;
}

.no-data-block {
  border-bottom: 1px solid $rfx-light-steel;
}

.common-table {
  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
  }
}

.edit-icon {
  cursor: pointer;
  font-size: 20px;
  justify-self: end;
  display: grid;
  color: $rfx-dark-march;
}

.download-icon {
  cursor: pointer;
  font-size: 20px;
  justify-self: end;
  display: grid;
  color: $rfx-dark-march;
}

</style>
